import { USER_APPROVE_STATUS, USER_QUALIFY_STATUS } from '@/config/constants';
import type { PropertyFilter } from '@/store/slices/propertyUiSlice';
import type { Product } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';
import type { User } from '@/types/UserType';

export type PortfolioProgressKind =
  | 'general'
  | 'purchase'
  | 'refinance'
  | 'investment'
  | 'rate_watch';

export type ProgressCheckItem = {
  label: string;
  value: boolean;
};

export type PortfolioProgressView = {
  kind: PortfolioProgressKind;
  title: string;
  subtitle: string;
  items: ProgressCheckItem[];
  /** When true, the portfolio progress card should not render. */
  complete: boolean;
  footerProductId?: string;
  footerProduct?: Product;
};

export function hasPropertyOffer(property: PropertyView): boolean {
  return Boolean(
    property.hasStartedContract || property.contractId || property.offerStatus
  );
}

function segmentForKind(
  kind: PortfolioProgressKind,
  properties: PropertyView[]
): PropertyView[] {
  switch (kind) {
    case 'general':
      return properties;
    case 'purchase':
      return properties.filter((p) => !p.isRefinance);
    case 'refinance':
      return properties.filter((p) => p.isRefinance);
    case 'investment':
      return properties.filter((p) => p.isDscr);
    case 'rate_watch':
      return properties.filter((p) => p.isRefinance);
    default:
      return [];
  }
}

function hasAdvancedPortfolioMilestone(allProperties: PropertyView[]): boolean {
  const purchases = segmentForKind('purchase', allProperties);
  const refinances = segmentForKind('refinance', allProperties);
  const investments = segmentForKind('investment', allProperties);

  if (purchases.some(hasPropertyOffer)) return true;
  if (hasSubmittedRefinanceApplication(refinances)) return true;
  if (investments.filter((p) => !p.isRefinance).some(hasPropertyOffer)) {
    return true;
  }
  return false;
}

/** Maps the My Properties filter chip to its progress tracker variant. */
export function filterToProgressKind(
  filter: PropertyFilter,
  user?: User | null
): PortfolioProgressKind {
  if (user?.onboardingIntent === 'rate_watch' && filter === 'refinance') {
    return 'rate_watch';
  }
  switch (filter) {
    case 'purchase':
      return 'purchase';
    case 'refinance':
      return 'refinance';
    case 'dscr':
      return 'investment';
    case 'all':
    default:
      return 'general';
  }
}

/** Real qualification numbers entered, not just a saved zip code (which any listing has). */
function hasCompletedFinancialProfile(
  user: User | null | undefined,
  products: Product[]
): boolean {
  const info = user?.loanInfo;
  if (info?.monthlyIncome || info?.creditScore || info?.downPayment) {
    return true;
  }
  return products.some((p) => Boolean(p.savedDownPayment));
}

function isUserQualified(user: User | null | undefined): boolean {
  return user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY;
}

function isUserApproved(user: User | null | undefined): boolean {
  return user?.approveStatus === USER_APPROVE_STATUS.APPROVED;
}

function hasSubmittedRefinanceApplication(segment: PropertyView[]): boolean {
  return segment.some((p) => Boolean(p.applicationId && p.stage >= 3));
}

function pickFooterTarget(
  segment: PropertyView[],
  products: Product[]
): Pick<PortfolioProgressView, 'footerProductId' | 'footerProduct'> {
  const sorted = [...segment].sort((a, b) => b.stage - a.stage);
  const lead = sorted[0];
  if (!lead) return {};
  const product = products.find((p) => p._id === lead.productId);
  if (!product) return {};
  return { footerProductId: lead.productId, footerProduct: product };
}

const COPY: Record<
  PortfolioProgressKind,
  { title: string; subtitle: string; steps: string[] }
> = {
  general: {
    title: 'Your progress',
    subtitle:
      'Overall milestones across purchases, refinances, and investments in your portfolio.',
    steps: [
      'Save a property to Unbeatable',
      'Complete your financial profile',
      'Check how much you can qualify for',
      'Receive a personal loan estimate',
      'Advance a property to application or offer',
    ],
  },
  purchase: {
    title: 'Purchase progress',
    subtitle:
      'Your overall purchase journey — we mark each step when you complete it on any home.',
    steps: [
      'Add a home to Unbeatable',
      'Complete a budget for guided shopping',
      'Check how much you can qualify for',
      'Receive a personal loan estimate',
      'Make an offer to buy a property',
    ],
  },
  refinance: {
    title: 'Refinance progress',
    subtitle:
      'Your overall refinance journey — progress reflects your furthest refinance listing.',
    steps: [
      'Add a property to refinance',
      'Complete your financial profile',
      'Check how much you can qualify for',
      'Receive a personal loan estimate',
      'Submit your refinance application',
    ],
  },
  investment: {
    title: 'Investment progress',
    subtitle:
      'Your overall investment journey — DSCR and portfolio steps across saved properties.',
    steps: [
      'Add an investment property',
      'Complete a budget for your portfolio',
      'Check how much you can qualify for',
      'Receive an investment loan estimate',
      'Make an offer on an investment property',
    ],
  },
  rate_watch: {
    title: 'Rate watch progress',
    subtitle:
      'Track market refinance rates against your current mortgage — upgrade when you are ready.',
    steps: [
      'Set up your rate watch',
      'Watch active — alerts when rates beat your mortgage',
      'Compare refinance options on your summary',
    ],
  },
};

export function buildPortfolioProgress(
  kind: PortfolioProgressKind,
  allProperties: PropertyView[],
  products: Product[],
  user: User | null | undefined
): PortfolioProgressView {
  const segment = segmentForKind(kind, allProperties);
  const { title, subtitle, steps } = COPY[kind];

  const values: boolean[] = (() => {
    switch (kind) {
      case 'general':
        return [
          allProperties.length > 0,
          hasCompletedFinancialProfile(user, products),
          isUserQualified(user),
          isUserApproved(user),
          hasAdvancedPortfolioMilestone(allProperties),
        ];
      case 'purchase':
        return [
          segment.length > 0,
          hasCompletedFinancialProfile(user, products),
          isUserQualified(user),
          isUserApproved(user),
          segment.some(hasPropertyOffer),
        ];
      case 'refinance':
        return [
          segment.length > 0,
          hasCompletedFinancialProfile(user, products),
          isUserQualified(user),
          isUserApproved(user),
          hasSubmittedRefinanceApplication(segment),
        ];
      case 'investment':
        return [
          segment.length > 0,
          hasCompletedFinancialProfile(user, products),
          isUserQualified(user),
          isUserApproved(user),
          segment.filter((p) => !p.isRefinance).some(hasPropertyOffer),
        ];
      case 'rate_watch': {
        const refiProducts = products.filter(
          (p) => p.transactionMode === 'refinance'
        );
        const hasWatchProperty = segment.length > 0;
        const hasCurrentRate = refiProducts.some(
          (p) => (p.refinance?.currentLoan?.rate ?? 0) > 0
        );
        const hasFullComparison = refiProducts.some(
          (p) => (p.refinance?.currentLoan?.balance ?? 0) > 0
        );
        return [
          hasWatchProperty,
          hasWatchProperty && hasCurrentRate,
          hasFullComparison,
        ];
      }
      default:
        return [];
    }
  })();

  const items = steps.map((label, i) => ({
    label,
    value: values[i] ?? false,
  }));

  const complete = items.length > 0 && items[items.length - 1].value;

  return {
    kind,
    title,
    subtitle,
    items,
    complete,
    ...pickFooterTarget(segment, products),
  };
}

/**
 * Progress card for the active My Properties filter chip. Each filter has its
 * own tracker; the card hides when that tracker's final milestone is complete.
 */
export function resolvePortfolioProgressView(
  filter: PropertyFilter,
  allProperties: PropertyView[],
  products: Product[],
  user: User | null | undefined
): PortfolioProgressView | null {
  if (allProperties.length === 0) return null;

  const view = buildPortfolioProgress(
    filterToProgressKind(filter, user),
    allProperties,
    products,
    user
  );

  return view.complete ? null : view;
}
